import type { Operaciones } from '../../api/operaciones';
import type { CampoLista, ColorLista, DatosTarifa, ListaInvitadosEvento, NivelTarifa, OperacionesListas, Plantilla, Rrpp, Tarifa } from '../../api/ops/listas';
import type { EnlaceRrpp, EventoPanel, Invitado, Liquidacion, OperacionesPortalRrpp } from '../../api/ops/portalRrpp';
import type { EstadoChip } from '../../componentes/Chip';
import type { TarjetaAyuda } from '../../marco/cabecera';
import { aCentimos, dinero } from '../../logica/dinero';
import { hoyLocal } from '../../logica/fechasCortas';

/**
 * Textos y reglas de «Listas y RRPP» (10.9 a 10.11), sin estilos: los mismos textos del diseño
 * (`diseno/eventos-entradas/10.9-10.11-listas-rrpp-liquidaciones.html`). El panel no mira el rol: decide por permisos.
 */

/** Las operaciones que usa la pantalla: las del panel, las suyas (`ops/listas.ts`) y las que comparte con el portal (10.12). */
export type OpsListas = Operaciones & OperacionesListas & OperacionesPortalRrpp;

/**
 * `crearOperaciones` junta `operacionesListas` y `operacionesPortalRrpp` (las da de alta la sesión que registra la
 * pantalla en `api/operaciones.ts`); hasta entonces el tipo de `PropsPantalla.ops` no las lleva.
 */
export function opsDe(ops: Operaciones): OpsListas {
    return ops;
}

export const PERMISOS = {
    listasVer: 'listas.listas.ver',
    listasGestionar: 'listas.listas.gestionar',
    plantillas: 'listas.plantillas.gestionar',
    invitadosVer: 'listas.invitados.ver',
    invitadosGestionar: 'listas.invitados.gestionar',
    enlacesVer: 'listas.enlaces.ver',
    enlacesGestionar: 'listas.enlaces.gestionar',
    asignar: 'listas.rrpp.asignar',
    rrppVer: 'rrpp.promotores.ver',
    rrppGestionar: 'rrpp.promotores.gestionar',
    rrppInvitar: 'rrpp.promotores.invitar',
    comisionesVer: 'rrpp.comisiones.ver',
    tarifas: 'rrpp.tarifas.gestionar',
    informes: 'rrpp.informes.ver',
    liquidacionesVer: 'rrpp.liquidaciones.ver',
    liquidacionesGestionar: 'rrpp.liquidaciones.gestionar',
    pagos: 'rrpp.pagos.registrar',
    pasesVer: 'pases.pases.ver',
    reenviar: 'pases.pases.reenviar',
    canales: 'canales.salud.ver',
    canalesGestionar: 'canales.canales.gestionar',
    webPublicar: 'web.sitio.publicar',
    equipoInvitaciones: 'equipo.miembros.gestionar',
} as const;

type Ayuda = readonly TarjetaAyuda[];
const a = (icono: TarjetaAyuda['icono'], titulo: string, texto: string): TarjetaAyuda => ({ icono, titulo, texto });

export const AYUDA: Readonly<Record<string, Ayuda>> = {
    listas: [
        a('list-checks', 'Listas del evento', 'Cada evento tiene sus listas, copiadas de tus plantillas: aforo propio, hora límite de entrada y cierre.'),
        a('instagram', 'Instagram o email', 'Para apuntar a alguien basta su usuario de Instagram o su email. WhatsApp, solo si el formulario web de la lista lo tiene activado.'),
        a('users', 'Aforo compartido', 'Los invitados ocupan aforo del evento igual que las entradas: si se llena, no se puede apuntar a nadie más.'),
    ],
    invitados: [
        a('send', 'Pase enviado', 'Por email, en el acto. Por Instagram, cuando el invitado escribe con su enlace personal.'),
        a('door-open', 'Ha llegado', 'Lo marca la puerta al escanear su pase.'),
        a('user-x', 'Retirar', 'Libera su plaza y anula su pase y su enlace. Solo con la lista abierta.'),
    ],
    plantillas: [
        a('layers', 'Plantillas', 'Las listas tipo de tu negocio. Las que van «a cada evento» se copian solas al crear un evento.'),
        a('pencil', 'Cambiarlas', 'No toca las listas ya creadas: cada evento guarda su copia.'),
        a('archive', 'Archivar', 'Deja de copiarse a los eventos nuevos. Nunca se borra.'),
    ],
    evento: [
        a('users', 'RRPP del evento', 'Dale a cada RRPP un cupo en el evento y en cada lista. Vacío = sin límite; 0 = no puede apuntar ahí.'),
        a('instagram', 'Su enlace del evento', 'Al asignarlo se crea su enlace de Instagram y su formulario web. Él lo copia desde su portal.'),
        a('refresh-cw', 'Si se filtra', 'Regenera el enlace. El viejo deja de funcionar para siempre, también donde ya esté publicado.'),
    ],
    todos: [
        a('user-plus', 'Alta de RRPP', 'Se invita como a cualquier persona del equipo, con el rol RRPP. Al aceptar aparece aquí.'),
        a('ticket', 'Código de venta', 'Las entradas que se compren con su enlace de venta cuentan para su comisión.'),
        a('power', 'Desactivar', 'Deja de apuntar invitados y de sumar ventas. Lo que tiene pendiente se le sigue liquidando.'),
    ],
    tarifas: [
        a('layers', 'La escalera', 'Manda la más específica: lista, evento, RRPP y, si no hay ninguna, la de la casa.'),
        a('percent', 'Cómo se calcula', 'Importe fijo, porcentaje de la venta o tramos por noche (solo RRPP y casa): el tramo alcanzado vale para todos los de esa noche.'),
        a('history', 'Cambiarla', 'Se guarda como versión nueva. No recalcula lo ya calculado ni lo liquidado.'),
    ],
    socio: [a('chart-column', 'Cifras de los RRPP', 'Invitados, llegadas, entradas y comisiones de cada RRPP, sin datos de los invitados.')],
    liquidaciones: [
        a('wallet', 'Liquidar', 'Elige RRPP y periodo: reunimos sus comisiones pendientes de esas noches. Las de una noche sin terminar no entran.'),
        a('lock', 'Cerrar', 'La cierras tú, cuando quieras. Al cerrarla el total queda congelado y avisamos al RRPP.'),
        a('banknote', 'Pagar', 'El dinero se lo das tú. Aquí anotas uno o varios pagos hasta completar el total.'),
    ],
};

// ---- Eventos ----

const DIAS = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
const MESES = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
const dos = (n: number) => String(n).padStart(2, '0');

/** «Sáb 17 oct · 21:30» (hora local del navegador, como el resto del panel). */
export function cuandoEs(iso: string | null | undefined): string {
    if (iso == null || iso === '') {
        return '';
    }
    const f = new Date(iso);
    if (Number.isNaN(f.getTime())) {
        return '';
    }
    return `${DIAS[f.getDay()] ?? ''} ${String(f.getDate())} ${MESES[f.getMonth()] ?? ''} · ${dos(f.getHours())}:${dos(f.getMinutes())}`;
}

/** «sáb 17 oct, 21:00» para «Se cierra el…». */
export function diaHora(iso: string | null | undefined): string {
    return cuandoEs(iso).replace(' · ', ', ').toLocaleLowerCase('es');
}

/** «23:30». */
export function hora(iso: string | null | undefined): string {
    if (iso == null || iso === '') {
        return '';
    }
    const f = new Date(iso);
    return Number.isNaN(f.getTime()) ? '' : `${dos(f.getHours())}:${dos(f.getMinutes())}`;
}

/** «17 oct». */
export function diaCorto(fechaLocal: string): string {
    const [, m, d] = fechaLocal.split('-').map(Number);
    return m === undefined || d === undefined ? fechaLocal : `${String(d)} ${MESES[m - 1] ?? ''}`;
}

export const ESTADO_EVENTO: Readonly<Record<EventoPanel['status'], [string, EstadoChip]>> = {
    published: ['Publicado', 'confirmed'],
    draft: ['Borrador', 'draft'],
    ended: ['Terminado', 'neutral'],
    cancelled: ['Cancelado', 'problem'],
};

/** El evento con el que abre la pantalla: el de `?evento=`, o el más próximo que no ha terminado, o el último. */
export function eventoInicial(eventos: readonly EventoPanel[], pedido: string | null, ahora: Date = new Date()): EventoPanel | null {
    const elegido = pedido === null ? undefined : eventos.find((e) => e.id === pedido);
    if (elegido !== undefined) {
        return elegido;
    }
    const orden = [...eventos].sort((x, y) => (x.starts_at ?? '').localeCompare(y.starts_at ?? ''));
    const vivo = orden.find((e) => e.status !== 'cancelled' && e.status !== 'ended' && new Date(e.ends_at ?? e.starts_at ?? 0).getTime() >= ahora.getTime());
    return vivo ?? orden[orden.length - 1] ?? null;
}

export function eventoAbierto(e: EventoPanel | null | undefined): boolean {
    return e != null && e.status !== 'ended' && e.status !== 'cancelled';
}

/** Las partes de la barra apilada del aforo (C9, como Informes 10.8): solo las que tienen algo. */
export function partesAforo(e: EventoPanel): { etiqueta: string; valor: number; color: string }[] {
    const c = e.capacity_detail;
    const partes = [
        { etiqueta: 'Vendidas', valor: c.tickets_confirmed, color: 'var(--rv-ink)' },
        { etiqueta: 'Pendientes de pago', valor: c.tickets_held, color: 'var(--rv-warning)' },
        { etiqueta: 'Invitados', valor: c.guests_held, color: 'var(--rv-fg-3)' },
        { etiqueta: 'Cupo de taquilla', valor: c.box_office_reserved, color: 'var(--rv-rule-strong)' },
    ];
    return partes.filter((p, i) => p.valor > 0 || i === 0 || i === 2);
}

export function personas(n: number): string {
    return n === 1 ? 'persona' : 'personas';
}

/**
 * Lo de debajo de «Llegaron» en 10.9 con el evento terminado (G992): «de M plazas · X %», plazas llegadas
 * (`arrived_people`) frente a plazas apuntadas (`people`); en la puerta se cuentan personas, no invitados.
 */
export function textoLlegaron(llegadas: number, apuntadas: number): string {
    return apuntadas > 0 ? `de ${String(apuntadas)} plazas · ${String(Math.round((llegadas / apuntadas) * 100))} %` : 'Nadie apuntado';
}

// ---- Listas ----

export const COLORES: Readonly<Record<ColorLista, [string, string]>> = {
    gray: ['var(--rv-mesa-grafito)', 'Gris'],
    red: ['var(--rv-red)', 'Rojo'],
    orange: ['var(--rv-mesa-naranja)', 'Naranja'],
    yellow: ['var(--rv-warning)', 'Amarillo'],
    green: ['var(--rv-mesa-verde)', 'Verde'],
    teal: ['var(--rv-mesa-turquesa)', 'Turquesa'],
    blue: ['var(--rv-mesa-azul)', 'Azul'],
    purple: ['var(--rv-mesa-morado)', 'Morado'],
    pink: ['var(--rv-mesa-rosa)', 'Rosa'],
};

export function colorDe(c: ColorLista | undefined): string {
    return COLORES[c ?? 'gray'][0];
}

export const ESTADO_LISTA: Readonly<Record<ListaInvitadosEvento['status'], [string, EstadoChip]>> = {
    open: ['Abierta', 'confirmed'],
    closed: ['Cerrada', 'neutral'],
    cancelled: ['Cancelada', 'problem'],
};

export function acompanantes(n: number | undefined): string {
    const k = n ?? 0;
    return k === 0 ? 'Sin acompañantes' : `Hasta ${String(k)} acompañante${k > 1 ? 's' : ''}`;
}

export function enPuerta(precio: ListaInvitadosEvento['door_price'] | undefined, mayuscula = false): string {
    if (precio == null || precio.amount === 0) {
        return mayuscula ? 'Gratis en puerta' : 'gratis en puerta';
    }
    return `${dinero(precio)} en puerta`;
}

/** La línea de debajo del nombre de la lista (10.9). */
export function lineaLista(l: ListaInvitadosEvento, eventoTerminado: boolean): string {
    if (l.status === 'cancelled') {
        return 'Cancelada';
    }
    if (l.status === 'closed') {
        return eventoTerminado ? 'Cerrada con el evento' : 'Cerrada: ya no se apunta nadie';
    }
    return `${acompanantes(l.max_companions)} · ${enPuerta(l.door_price)}`;
}

export function apuntadosLista(l: Pick<ListaInvitadosEvento, 'guests_count' | 'capacity'>): string {
    return l.capacity == null ? `${String(l.guests_count)} · sin límite propio` : `${String(l.guests_count)} de ${String(l.capacity)}`;
}

export function quienApunta(l: ListaInvitadosEvento, rrppConCupo: number): [string, string] {
    if (l.public_signup !== true) {
        return ['Solo el equipo y sus RRPP', rrppConCupo > 0 ? `${String(rrppConCupo)} RRPP con cupo` : 'Sin RRPP'];
    }
    return ['Instagram y web', l.web_form_delivery === 'whatsapp' ? 'La web manda el pase por WhatsApp' : rrppConCupo > 0 ? `${String(rrppConCupo)} RRPP con cupo` : 'Sin RRPP'];
}

/** Preguntas que nunca se pueden hacer (G49). */
export function preguntaProhibida(texto: string): boolean {
    return /tel[eé]fono|m[oó]vil|whatsapp|salud|alergi|m[eé]dic/i.test(texto);
}

export interface FormularioLista {
    nombre: string;
    color: ColorLista;
    aforo: string;
    acompanantes: number;
    horaLimite: string;
    cierre: string;
    puerta: string;
    publica: boolean;
    envio: 'email' | 'whatsapp';
    entendido: boolean;
    campos: CampoLista[];
    aCadaEvento: boolean;
}

export function formularioDeLista(origen: (ListaInvitadosEvento | Plantilla) | null, comoHora: (iso: string | null | undefined) => string = (x) => x ?? ''): FormularioLista {
    // La lista trae `door_cutoff_at` y `closes_at`; la plantilla, `door_cutoff_local_time` y `apply_by_default`.
    const l = origen as (Partial<ListaInvitadosEvento> & Partial<Plantilla>) | null;
    const esLista = l !== null && l.event_id !== undefined;
    return {
        nombre: l?.name ?? '',
        color: l?.color ?? 'gray',
        aforo: l?.capacity == null ? '' : String(l.capacity),
        acompanantes: l?.max_companions ?? 1,
        horaLimite: l === null ? '23:30' : esLista ? comoHora(l.door_cutoff_at) : (l.door_cutoff_local_time ?? ''),
        cierre: esLista ? comoHora(l.closes_at) : '',
        puerta: l?.door_price == null || l.door_price.amount === 0 ? '' : (l.door_price.amount / 100).toLocaleString('es-ES', { minimumFractionDigits: 2, useGrouping: false }),
        publica: l?.public_signup ?? false,
        envio: l?.web_form_delivery ?? 'email',
        entendido: l?.web_form_delivery === 'whatsapp',
        campos: l?.fields ?? [],
        aCadaEvento: l?.apply_by_default ?? false,
    };
}

export function erroresLista(f: FormularioLista): Partial<Record<'nombre' | 'aforo' | 'puerta' | 'entendido', string>> {
    const e: Partial<Record<'nombre' | 'aforo' | 'puerta' | 'entendido', string>> = {};
    if (f.nombre.trim().length < 2) {
        e.nombre = 'Escribe el nombre de la lista.';
    }
    if (f.aforo.trim() !== '' && !/^\d+$/.test(f.aforo.trim())) {
        e.aforo = 'Un número entero o vacío.';
    }
    if (f.puerta.trim() !== '' && aCentimos(f.puerta) === null) {
        e.puerta = 'Escribe un importe, por ejemplo 10 o 12,50.';
    }
    if (f.publica && f.envio === 'whatsapp' && !f.entendido) {
        e.entendido = 'Confirma que lo entiendes.';
    }
    return e;
}

/** Los ajustes comunes de lista y plantilla para la API (sin la hora límite ni el cierre, que van en cada uno). */
export function ajustesDeFormulario(f: FormularioLista, moneda: string) {
    const centimos = f.puerta.trim() === '' ? null : aCentimos(f.puerta);
    return {
        name: f.nombre.trim(),
        color: f.color,
        capacity: f.aforo.trim() === '' ? null : Number(f.aforo.trim()),
        max_companions: f.acompanantes,
        door_price: centimos === null || centimos === 0 ? null : { amount: centimos, currency: moneda },
        public_signup: f.publica,
        web_form_delivery: f.publica ? f.envio : ('email' as const),
        acknowledge_whatsapp_cost: f.publica && f.envio === 'whatsapp' && f.entendido,
        fields: f.campos,
    };
}

// ---- Invitados ----

export const ORIGEN: Readonly<Record<Invitado['origin'], string>> = {
    promoter: 'por su RRPP',
    staff: 'por el equipo',
    generic_link: 'por el enlace de Instagram del RRPP',
    web_form: 'en el formulario web del RRPP',
};

/** Estado del invitado: texto, chip (solo si hay que mirarlo) y detalle (G518, G755, G788). */
export function estadoInvitado(g: Invitado, eventoTerminado: boolean): { texto: string; chip: EstadoChip | null; detalle: string } {
    if (g.status === 'withdrawn') {
        return { texto: 'Retirado', chip: 'problem', detalle: g.withdrawn_by_guest === true ? 'Salió él mismo con el enlace del aviso de privacidad' : '' };
    }
    if (g.status === 'cancelled') {
        return { texto: 'Cancelado', chip: 'problem', detalle: 'Se canceló su lista' };
    }
    if (g.arrived_at != null) {
        return { texto: 'Ha llegado', chip: null, detalle: hora(g.arrived_at) };
    }
    if (eventoTerminado) {
        return { texto: 'No vino', chip: null, detalle: '' };
    }
    if (g.pass_state === 'waiting_instagram') {
        return { texto: 'Apuntado', chip: 'neutral', detalle: 'Falta que escriba por Instagram' };
    }
    if (g.pass_state === 'delivered') {
        return { texto: 'Pase enviado', chip: null, detalle: g.contact_channel === 'instagram' ? 'En el chat de Instagram' : g.contact_channel === 'whatsapp' ? 'Por WhatsApp' : 'Por email' };
    }
    return { texto: 'Apuntado', chip: 'neutral', detalle: '' };
}

/** Contacto del invitado: el canal (la API no da el usuario ni el email, ver informe) o «Sin contacto» (G518). */
export function contactoInvitado(g: Invitado): [string, string] {
    if (g.customer_id === null) {
        return ['Sin contacto', 'Ya no tiene ficha de cliente'];
    }
    if (g.contact_channel === 'instagram') {
        return ['Instagram', g.instagram_linked === true ? 'Ya escribió' : ''];
    }
    if (g.contact_channel === 'whatsapp') {
        return ['Teléfono del formulario web', 'Formulario web'];
    }
    return ['Email', ''];
}

/**
 * Filtros de estado de la vista Invitados, como los admite `listGuests` (`status`, `pass_state`, G788; `arrived`, G836).
 * «Ha llegado» y «No vino», en el orden del dibujo de 10.9 (G904): `arrived=true` y los apuntados con `arrived=false`.
 */
export const FILTROS_INVITADOS: readonly { valor: string; texto: string; consulta: { status?: Invitado['status']; pass_state?: Invitado['pass_state']; arrived?: boolean } }[] = [
    { valor: '', texto: 'Todos', consulta: {} },
    { valor: 'apuntado', texto: 'Apuntado', consulta: { status: 'active', pass_state: 'waiting_instagram' } },
    { valor: 'enviado', texto: 'Pase enviado', consulta: { status: 'active', pass_state: 'delivered' } },
    { valor: 'llegado', texto: 'Ha llegado', consulta: { arrived: true } },
    { valor: 'no-vino', texto: 'No vino', consulta: { status: 'active', arrived: false } },
    { valor: 'retirado', texto: 'Retirado', consulta: { status: 'withdrawn' } },
    { valor: 'cancelado', texto: 'Cancelado', consulta: { status: 'cancelled' } },
];

export interface FormularioApuntar {
    lista: string;
    nombre: string;
    canal: 'instagram' | 'email';
    instagram: string;
    email: string;
    conEmail: boolean;
    acompanantes: number;
    rrpp: string;
    respuestas: Record<string, string>;
}

const EMAIL = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

export function erroresApuntar(f: FormularioApuntar, campos: readonly CampoLista[] = []): Partial<Record<string, string>> {
    const e: Partial<Record<string, string>> = {};
    if (f.lista === '') {
        e.lista = 'Elige la lista.';
    }
    if (f.nombre.trim().length < 2) {
        e.nombre = 'Escribe su nombre.';
    }
    if (f.canal === 'instagram') {
        if (!/^@?[A-Za-z0-9._]{1,30}$/.test(f.instagram.trim())) {
            e.instagram = f.instagram.trim() === '' ? 'Escribe su usuario de Instagram.' : 'Revisa el usuario: letras, números, puntos y _.';
        }
        if (f.conEmail && f.email.trim() !== '' && !EMAIL.test(f.email.trim())) {
            e.email = 'Revisa el email.';
        }
    } else if (!EMAIL.test(f.email.trim())) {
        e.email = f.email.trim() === '' ? 'Escribe su email.' : 'Revisa el email.';
    }
    for (const c of campos) {
        if (c.required === true && (f.respuestas[c.label] ?? '').trim() === '') {
            e[`campo:${c.label}`] = 'Responde a esta pregunta.';
        }
    }
    return e;
}

export function altaDeFormulario(f: FormularioApuntar) {
    const ig = f.instagram.trim().replace(/^@/, '');
    const email = f.email.trim();
    return {
        guest_list_id: f.lista,
        name: f.nombre.trim(),
        ...(f.canal === 'instagram' ? { instagram_username: ig, preferred_channel: 'instagram' as const } : { preferred_channel: 'email' as const }),
        ...((f.canal === 'email' || (f.conEmail && email !== '')) ? { email } : {}),
        companions: f.acompanantes,
        ...(f.rrpp === '' ? {} : { member_id: f.rrpp }),
        answers: Object.entries(f.respuestas).filter(([, v]) => v.trim() !== '').map(([label, value]) => ({ label, value: value.trim() })),
    };
}

/** Sin `https://` (G125). */
export function sinProtocolo(url: string | null | undefined): string {
    return (url ?? '').replace(/^https?:\/\//, '');
}

// ---- RRPP ----

export function nombreRrpp(p: { display_name?: string | null; sales_code?: string } | undefined): string {
    if (p === undefined) {
        return 'Su RRPP';
    }
    return p.display_name != null && p.display_name !== '' ? p.display_name : `RRPP ${p.sales_code ?? ''}`.trim();
}

export function cupoTexto(cupo: number | null | undefined, usado: number): string {
    return cupo == null ? `${String(usado)} · sin límite` : `${String(usado)} de ${String(cupo)}`;
}

/** Texto de un cupo escrito: vacío = sin límite (null), número entero ≥ 0; undefined si no es válido. */
export function cupoEscrito(v: string): number | null | undefined {
    const t = v.trim();
    if (t === '') {
        return null;
    }
    return /^\d+$/.test(t) ? Number(t) : undefined;
}

export const BASE: Readonly<Record<DatosTarifa['basis'], [string, TarjetaAyuda['icono']]>> = {
    guest_arrival: ['Invitado que entra', 'door-open'],
    ticket_sold: ['Entrada vendida', 'ticket'],
    ticket_arrival: ['Entrada que entra', 'scan-line'],
    reservation: ['Reservado vendido', 'armchair'],
};

export const NIVEL: Readonly<Record<NivelTarifa, [string, TarjetaAyuda['icono']]>> = {
    list: ['Lista', 'list-checks'],
    event: ['Evento', 'calendar'],
    promoter: ['RRPP', 'user'],
    house: ['Casa', 'store'],
};

export const ORDEN_NIVELES: readonly NivelTarifa[] = ['list', 'event', 'promoter', 'house'];

/** «Cómo se calcula» de una tarifa (G520: fijo por entrada, por reserva o por persona). */
export function comoSeCalcula(t: Pick<Tarifa, 'basis' | 'amount' | 'percent_bps' | 'tiers'>): [string, string] {
    const por = t.basis === 'ticket_sold' ? 'por entrada' : t.basis === 'reservation' ? 'por reserva' : 'por persona';
    if (t.percent_bps != null) {
        return [`${(t.percent_bps / 100).toLocaleString('es-ES', { maximumFractionDigits: 2 })} % de la venta`, 'Sin gastos de gestión'];
    }
    if (t.tiers != null && t.tiers.length > 0) {
        return ['Por tramos de la noche', t.tiers.map((x) => `${String(x.from)}${x.to == null ? '+' : `–${String(x.to)}`}: ${dinero(x.amount)}`).join(' · ')];
    }
    return [`${dinero(t.amount)} ${por}`, ''];
}

export interface FormularioTarifa {
    nivel: NivelTarifa;
    rrpp: string;
    evento: string;
    lista: string;
    base: DatosTarifa['basis'];
    como: 'amount' | 'pct' | 'tiers';
    importe: string;
    porcentaje: string;
    tramos: { desde: string; hasta: string; importe: string }[];
}

export function erroresTarifa(f: FormularioTarifa): Partial<Record<string, string>> {
    const e: Partial<Record<string, string>> = {};
    if (f.nivel === 'promoter' && f.rrpp === '') {
        e.rrpp = 'Elige el RRPP.';
    }
    if (f.nivel === 'event' && f.evento === '') {
        e.evento = 'Elige el evento.';
    }
    if (f.nivel === 'list' && f.lista === '') {
        e.lista = 'Elige la lista.';
    }
    if (f.como === 'amount' && !((aCentimos(f.importe) ?? 0) > 0)) {
        e.importe = 'Escribe el importe, por ejemplo 1,50.';
    }
    if (f.como === 'pct') {
        const v = Number(f.porcentaje.replace(',', '.'));
        if (!(v > 0 && v <= 100)) {
            e.porcentaje = 'Entre 0,01 y 100.';
        }
    }
    if (f.como === 'tiers') {
        f.tramos.forEach((t, i) => {
            if (!((aCentimos(t.importe) ?? 0) > 0)) {
                e[`tramo${String(i)}`] = 'Escribe el importe del tramo.';
            } else if (i < f.tramos.length - 1 && !(Number(t.hasta) >= Number(t.desde))) {
                e[`tramo${String(i)}`] = '«Hasta» no puede ser menor que «Desde».';
            }
        });
    }
    return e;
}

export function tarifaDeFormulario(f: FormularioTarifa, moneda: string): DatosTarifa {
    const destino = f.nivel === 'promoter' ? { promoter_id: f.rrpp } : f.nivel === 'event' ? { event_id: f.evento } : f.nivel === 'list' ? { guest_list_id: f.lista } : {};
    const calculo =
        f.como === 'amount'
            ? { amount: { amount: aCentimos(f.importe) ?? 0, currency: moneda } }
            : f.como === 'pct'
              ? { percent_bps: Math.round(Number(f.porcentaje.replace(',', '.')) * 100) }
              : {
                    tiers: f.tramos.map((t, i) => ({
                        from: i === 0 ? 1 : Number(f.tramos[i - 1]?.hasta ?? 0) + 1,
                        to: i === f.tramos.length - 1 ? null : Number(t.hasta),
                        amount: { amount: aCentimos(t.importe) ?? 0, currency: moneda },
                    })),
                };
    return { level: f.nivel, basis: f.base, ...destino, ...calculo };
}

// ---- Liquidaciones ----

export const FORMA_PAGO: Readonly<Record<'cash' | 'bank_transfer' | 'other', [string, TarjetaAyuda['icono']]>> = {
    cash: ['Efectivo', 'banknote'],
    bank_transfer: ['Transferencia', 'landmark'],
    other: ['Otra forma', 'wallet'],
};

/**
 * Lo que queda por pagar de una liquidación cerrada (total − pagado, en céntimos y con la moneda de la API). Es la única
 * resta de dinero del panel en esta pantalla: la API no lo da (pregunta en el informe).
 */
export function quedaPorPagar(l: Pick<Liquidacion, 'total' | 'paid_total'>): { amount: number; currency: string } {
    return { amount: l.total.amount - l.paid_total.amount, currency: l.total.currency };
}

export function chipLiquidacion(l: Liquidacion): [string, EstadoChip] | null {
    if (l.status === 'draft') {
        return ['Borrador', 'draft'];
    }
    if (l.status === 'discarded') {
        return ['Descartada', 'neutral'];
    }
    const queda = quedaPorPagar(l).amount;
    if (queda <= 0) {
        return ['Pagada', 'confirmed'];
    }
    return l.paid_total.amount > 0 ? ['Pagada en parte', 'pending'] : ['Por pagar', 'pending'];
}

export function periodo(l: Pick<Liquidacion, 'period_from' | 'period_to'>): string {
    return `${diaCorto(l.period_from)} – ${diaCorto(l.period_to)}`;
}

/** Error del importe de un pago (no más de lo que queda; una corrección, no más de lo pagado). */
export function errorPago(texto: string, negativo: boolean, l: Pick<Liquidacion, 'total' | 'paid_total'>): string | null {
    const c = aCentimos(texto.replace('−', '').replace('-', ''));
    if (c === null || c === 0) {
        return 'Escribe el importe.';
    }
    const queda = quedaPorPagar(l);
    if (!negativo && c > queda.amount) {
        return `Como mucho ${dinero(queda)}: es lo que queda por pagar.`;
    }
    if (negativo && c > l.paid_total.amount) {
        return `Como mucho ${dinero(l.paid_total)}: es lo pagado.`;
    }
    return null;
}

/** Importe de un ajuste: admite el signo menos («−5,00» o «-5»). null si no es válido o es 0. */
export function importeAjuste(texto: string): number | null {
    const t = texto.trim();
    const negativo = /^[−-]/.test(t);
    const c = aCentimos(t.replace(/^[−-]/, ''));
    if (c === null || c === 0) {
        return null;
    }
    return negativo ? -c : c;
}

// ---- Movidas aquí desde las pantallas (sin dibujo) ----

/** Por qué un enlace de RRPP no tiene dirección (`unavailable_reasons`, G788). */
export function motivosDeEnlace(e: EnlaceRrpp): string[] {
    const r = e.unavailable_reasons ?? [];
    return [r.includes('instagram_not_connected') ? 'Instagram sin conectar' : '', r.includes('site_not_published') ? 'web sin publicar' : ''].filter((x) => x !== '');
}

/** «Para» de una tarifa: el destino de su escalón. */
export function paraQuien(t: Tarifa, ctx: { rrpp: readonly Rrpp[]; eventos: readonly EventoPanel[] }): [string, string] {
    if (t.level === 'house') {
        return ['Casa', 'Cualquier RRPP, si no hay otra'];
    }
    if (t.level === 'promoter') {
        return [nombreRrpp(ctx.rrpp.find((p) => p.id === t.promoter_id)), 'Este RRPP, en todos los eventos'];
    }
    if (t.level === 'event') {
        const e = ctx.eventos.find((x) => x.id === t.event_id);
        return [e?.name ?? 'Evento', `Todos los RRPP${e === undefined ? '' : ` · ${cuandoEs(e.starts_at)}`}`];
    }
    return ['Lista', 'Todos los RRPP en esta lista'];
}

export type PeriodoSocio = 'mes' | 'anterior' | 'ano';
export const PERIODOS_SOCIO = ['mes', 'anterior', 'ano'] as const;

/** Periodo de las cifras del Socio en fechas locales (`AAAA-MM-DD`). */
export function periodoSocio(p: PeriodoSocio, ahora: Date = new Date()): { from: string; to: string; texto: string } {
    const nombre = (d: Date) => d.toLocaleDateString('es-ES', { month: 'long', year: 'numeric' }).replace(/^./, (c) => c.toLocaleUpperCase('es'));
    if (p === 'anterior') {
        const ini = new Date(ahora.getFullYear(), ahora.getMonth() - 1, 1);
        const fin = new Date(ahora.getFullYear(), ahora.getMonth(), 0);
        return { from: hoyLocal(ini), to: hoyLocal(fin), texto: nombre(ini) };
    }
    if (p === 'ano') {
        return { from: hoyLocal(new Date(ahora.getFullYear(), 0, 1)), to: hoyLocal(ahora), texto: 'Este año' };
    }
    return { from: hoyLocal(new Date(ahora.getFullYear(), ahora.getMonth(), 1)), to: hoyLocal(ahora), texto: nombre(ahora) };
}

/** Periodos rápidos de «Preparar liquidación» (10.11). */
export function periodosRapidos(ahora: Date = new Date()): { texto: string; desde: string; hasta: string }[] {
    const anterior = new Date(ahora.getFullYear(), ahora.getMonth() - 1, 1);
    const mes = (d: Date) => d.toLocaleDateString('es-ES', { month: 'long' }).replace(/^./, (c) => c.toLocaleUpperCase('es'));
    return [
        { texto: mes(anterior), desde: hoyLocal(anterior), hasta: hoyLocal(new Date(ahora.getFullYear(), ahora.getMonth(), 0)) },
        { texto: `${mes(ahora)} hasta hoy`, desde: hoyLocal(new Date(ahora.getFullYear(), ahora.getMonth(), 1)), hasta: hoyLocal(ahora) },
        { texto: 'Todo lo pendiente', desde: hoyLocal(new Date(ahora.getFullYear() - 1, 0, 1)), hasta: hoyLocal(ahora) },
    ];
}

/** «Mensajes › Canales de envío» (5.8, entrada `mensajes_canales`): ahí se enciende «Enviar por WhatsApp» (G562). */
export const RUTA_CANALES_DE_ENVIO = '/mensajes?apartado=canales';
